import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Modal, Form, message, Input, Cascader, Button, Upload, Select, Progress } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import {
  FileRvtColor,
  FileRteColor,
  FileDefaultColor,
  FileRfaColor,
  FileSkpColor,
  Close,
  WarmingCircle,
  EmptyMemberColor,
  FileGlbColor,
  Property,
} from 'patternx-svg';
import { useUpload } from '@/hooks';
import classnames from 'classnames';
// type
import type { UploadProps } from 'antd';
import type { UploadFile, UploadChangeParam } from 'antd/lib/upload/interface';
// util
import { getUploadUrl } from '@/services/initial';
import { computeMD5, deepfind, extractVersion } from '../../utils';
import myCustomRequest from '@/utils/uploadRequest';
import { updateComponent } from '@/services/source';
import { getUUID } from '@/utils/utils';

// styles
import styles from './index.module.less';
// const multiTagFileTypes = ['skp', 'gltf', 'glb'];
import UploadBtn from '../add/upload_btn';
import {
  isGmodelerFile,
  allowSuffix,
  isGmodelerFileWithId,
  isGmodelerFileZip,
} from '@/components/upload-list/check';
import jszip from 'jszip';
import AdsorptionModal from '../adsorption';

interface IPorps {
  componentData: ELEMENT.IComponentDetail;
  visible: boolean;
  getElementsFirst: () => void;
  setUpdateInfo: (v: any) => void;
  defaultTags: any[];
  defaultapps: any[];
  adsorptionCategorys: any[];
  referencePointHorizontals: any[];
  referencePointVerticals: any[];
  referenceFaceVerticals: any[];
  referenceFaceHorizontals: any[];
  targetHosts: any[];
}
interface IFile extends UploadFile {
  md5?: string;
  extra: any;
  pid: React.Key;
  batchId: string;
  webkitRelativePath: string;
  isDirectory?: boolean;
  children?: any[];
  dirId?: React.Key;
  [key: string]: any;
}
function Index({
  componentData,
  visible = false,
  setUpdateInfo,
  getElementsFirst,
  defaultTags,
  defaultapps,
  adsorptionCategorys,
  referencePointHorizontals,
  referencePointVerticals,
  referenceFaceVerticals,
  referenceFaceHorizontals,
  targetHosts,
}: IPorps) {
  const [form] = Form.useForm();

  // 每次进入时需要获取最新的标签列表
  const [tags, setTags] = useState<any[]>(defaultTags || []);
  const [apps, setApps] = useState<any[]>(defaultapps || []);

  // 本地状态
  const [info, setInfo] = useState<UpdateComponent.IInfo>({
    saveIsLoading: false,
    isRefreshDetails: false,
    saveIsDisabled: false,
    isChangeDetail: false,
  });

  //正向类构件
  const fileRef = useRef({} as HTMLDivElement);
  const folderRef = useRef({} as HTMLDivElement);
  const [uploads, setUploads] = useState<IFile[]>([]);
  let isWarning = false;

  //行为规则参数
  const [isShowAdsorptionModal, setIsShowAdsorptionModal] = useState(false);
  const [extData, setExtData] = useState<any>({
    extData: componentData?.extData || {},
    adsorptionCategory: componentData?.adsorptionCategory || undefined,
  });
  const [isAbleSet, setIsAbleSet] = useState(componentData.isAllowSetting);

  // 文件 上传的文件.isall
  const [fileList, setFileList] = useState<
    (UploadFile & {
      md5?: string;
      storageFileId?: string;
      fileSuffix?: string;
      filePath?: string;
    })[]
  >(() => []);

  // 获取层级标签
  const tagsArr = useMemo(() => {
    if (!tags.length) return;
    function getName(ele: any, labels: any[]) {
      if (ele?.pid) {
        const activeElement = deepfind(tags, 'id', ele.pid);
        if (activeElement?.id) {
          labels.unshift(activeElement.id);
        }
        getName(activeElement, labels);
      }
    }
    const mapResult: any[] = [];
    componentData.tagIdList?.forEach((curTag: any) => {
      const ele = deepfind(tags, 'id', curTag);
      if (ele) {
        mapResult.unshift(ele.id);
        if (ele?.pid) {
          getName(ele, mapResult);
        }
      }
    });
    return mapResult;
  }, [componentData.tagIdList, tags]);

  // 文件上传功能
  const fileProps: UploadProps = {
    action: (file: any) => {
      return new Promise((resolve) => {
        resolve(file?.uploadUrl || '');
      });
    },
    accept: `.rfa, .skp, .gltf, .glb, .max, .zip`,
    method: 'PUT',
    maxCount: 1,
    customRequest: (options: any) => {
      if (options?.file?.percent === 100 || !options?.file?.uploadUrl) {
        return;
      }
      myCustomRequest(options);
    },
    showUploadList: false,
    beforeUpload: async (file: any) => {
      setFileList(() => [{ ...file, fileName: file.name, status: 'uploading', percent: 0 }]);
      // form.setFieldsValue({ tagsArr: [] });
      // setCurTagValue([]);

      const [version, md5] = (await Promise.all([
        extractVersion(file),
        computeMD5(file),
      ])) as string[];
      // const md5 = (await computeMD5(file)) as string;
      const fileSuffix = (file?.name?.split('.')?.pop() ?? 'rvt').toLowerCase();
      const res: any = await getUploadUrl('/boss/api/v1/component/public/file:upload', {
        enterpriseId: '',
        appName: 'boss',
        fileName: file?.name,
        fileSuffix,
        fullPath: '/test/',
        fileSize: file?.size,
        md5,
        fileVersion: version,
        fileType: 'buildingFile',
      });
      if (res?.success) {
        const datas = res?.data?.details;
        file.uploadUrl = datas?.url;
        file.exists = datas?.exists;
        file.md5 = md5;
        file.storageFileId = datas?.storage?.storageFileId;
        file.fileSuffix = fileSuffix;
        file.fullPath = file.originFileObj?.webkitRelativePath;
        setFileList(() => [{ ...file, percent: 0, status: 'uploading' }]);
      } else {
        setFileList(() => [{ ...file, status: 'error', cmd5: false }]);
        return Upload.LIST_IGNORE;
      }
      return true;
    },
    // 文件上传的进度条
    onChange: async (fileInfo: UploadChangeParam) => {
      const { fileList: mapFileList } = fileInfo;
      mapFileList.forEach((r: any) => {
        if (r?.exists) {
          r.percent = 100;
        }
      });
      setFileList([...mapFileList]);
      // 模拟进度条 100%
      setTimeout(() => {
        setFileList((v) =>
          v.map((item) => {
            if (item.percent === 100 && item.status !== 'done') {
              return { ...item, status: 'done' };
            } else {
              return item;
            }
          }),
        );
      }, 100);
    },
  };

  // Form 组织详情 更新提交失败事件
  const onFieldsChange = (changedFields: any[], a: any) => {
    // setInfo((v) => ({ ...v, isChangeDetail: true }));
    if (changedFields.length) {
      if (changedFields[0].errors.length) {
        setInfo((v) => ({ ...v, saveIsDisabled: true }));
      } else {
        setInfo((v) => ({ ...v, saveIsDisabled: false }));
      }
    }
  };

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        componentName: componentData?.name,
        tagsArr,
        appList: componentData?.appIdList,
        adsorptionCategory: componentData?.adsorptionCategory || undefined,
      });
      // getLabels();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const renderFileIcon = (file: any) => {
    // console.log(file, '===file');
    const name = (file?.fileName ?? file?.name)?.toLowerCase();
    if (name?.endsWith('.rvt')) {
      return <FileRvtColor size={40} />;
    }
    if (name?.endsWith('.rte')) {
      return <FileRteColor size={40} />;
    }
    if (name?.endsWith('.rfa')) {
      return <FileRfaColor size={40} />;
    }
    if (name?.endsWith('.skp')) {
      return <FileSkpColor size={40} />;
    }
    if (name?.endsWith('.glb')) {
      return <FileGlbColor size={40} />;
    }
    return <FileDefaultColor size={40} />;
  };

  const { uploadRequest } = useUpload();

  const uploadFile = (file: IFile): Promise<void> => {
    const fileSuffix = (file?.name?.split('.')?.pop() ?? 'rvt').toLowerCase();
    return new Promise((resolve, reject) => {
      uploadRequest({
        appName: 'boss',
        url: '/boss/api/v1/component/public/file:upload',
        fileStream: file?.fileStream || file,
        enterpriseId: '',
        fileName: `${file.name.slice(0, file.name.lastIndexOf('.'))}.${fileSuffix}`,
        fileSuffix,
        fullPath: '/test/',
        fileSize: file?.size,
        fileType: 'document',
        onPreload: (xhr: XMLHttpRequest, storage: any) => {
          file.extra = storage.storageFileId;
          file.storageId = storage.storageId;
        },
        onProgress: (res: any) => {
          setUploads((prevState) => {
            return prevState.map((item: any) => {
              if (item.uid === file.uid) {
                item.percent = res.percent;
                item.status = 'uploading';
              }
              return item;
            });
          });
        },
        onSuccess: () => {
          setUploads((prevState) => {
            return prevState.map((item: any) => {
              if (item.uid === file.uid) {
                item.percent = 100;
                item.status = 'done';
              }
              return item;
            });
          });
          // saveFile(file);
          resolve();
        },
        onError: (error: any) => {
          const { exists, storage }: { exists: boolean; storage: any } = error;
          setUploads((prevState) => {
            return prevState.map((item: any) => {
              if (item.uid === file.uid) {
                if (exists) {
                  item.percent = 100;
                  item.status = 'done';
                } else {
                  item.error = '上传失败。发生未知错误，请重新上传';
                }
              }
              return item;
            });
          });

          if (exists) {
            file.extra = storage.storageFileId;
            file.storageId = storage.storageId;
            // saveFile(file);
            resolve();
          } else {
            reject();
          }
        },
      });
    });
  };

  const uploadFileList = (files: any[]) => {
    files?.forEach((f) => {
      uploadFile(f);
    });
  };

  const getJsonAnother = async (file: any) => {
    return new Promise((r) => {
      const reader = new FileReader();
      reader.readAsText(file, 'UTF-8');
      reader.onload = (evt) => {
        const fileString: any = evt?.target?.result || '';
        const jsonObj = JSON.parse(fileString);
        const elementType = jsonObj?.elementType;
        const isHasElementType = Object.keys(jsonObj)?.includes('elementType');
        const isAllowSetting =
          !isHasElementType || (isHasElementType && (elementType == 316 || elementType == 318));
        const isHasReference =
          Object.keys(jsonObj)?.includes('referencePointHorizontal') ||
          Object.keys(jsonObj)?.includes('referencePointVertical') ||
          Object.keys(jsonObj)?.includes('referenceFaceVertical') ||
          Object.keys(jsonObj)?.includes('referenceFaceHorizontal') ||
          Object.keys(jsonObj)?.includes('targetHost');
        r({
          name: jsonObj.name || '',
          id: jsonObj.guid || '',
          familyType: jsonObj.familyType,
          referencePointHorizontal: jsonObj.referencePointHorizontal || '',
          referencePointVertical: jsonObj.referencePointVertical || '',
          referenceFaceVertical: jsonObj.referenceFaceVertical || '',
          referenceFaceHorizontal: jsonObj.referenceFaceHorizontal || '',
          targetHost: jsonObj.targetHost,
          isAllowSetting,
          isHasReference,
        });
      };
    });
  };

  const getZipFiles = async (file: any, res: any[], rootName?: string) => {
    const zips = await jszip.loadAsync(file, {
      decodeFileName: function (bytes) {
        const iconv = require('iconv-lite');
        return iconv.decode(bytes, 'gbk'); // 按中文编码
      },
    });
    const zfiles: any = zips.files;
    for (let k in zfiles) {
      const cFile = zfiles[k];
      const suffix = (cFile.name?.split('.')?.pop() || '').toLowerCase();
      let fileName = '',
        folderPath = '';
      if (cFile.name?.includes('/') && !cFile.name?.endsWith('/')) {
        fileName = cFile.name?.split('/')?.pop() || '';
        folderPath = cFile.name?.replace(`/${fileName}`, '');
      } else {
        fileName = cFile.name;
        folderPath = file.directoryName;
      }

      if (!cFile.dir && allowSuffix.includes(suffix)) {
        cFile.folderPath = `${rootName ? rootName + '/' : ''}${folderPath}`;
        cFile.fileSuffix = suffix;
        cFile.suffix = suffix;
        cFile.name = fileName;
        cFile.fileType = 'gmodeler';
        cFile.md5 = '';
        cFile.percent = 0;
        cFile.status = 'uploading';
        if (!cFile.uid) {
          cFile.uid = `new-file-${getUUID()}`;
        }
        const fileBlob = await zips.file(k)?.async('blob');
        if (fileName?.toLowerCase() === 'meta.json') {
          // const name = await getJsonName(fileBlob);
          // cFile.familyType = familyType || '';
          const {
            name,
            familyType,
            referenceFaceVertical,
            referenceFaceHorizontal,
            targetHost,
            referencePointHorizontal,
            referencePointVertical,
            isAllowSetting,
            isHasReference,
          } = (await getJsonAnother(fileBlob)) as any;
          const folderName = folderPath.split('/').pop();
          cFile.folderName = name || folderName;
          cFile.familyType = familyType;
          if (isAllowSetting) {
            cFile.referenceFaceVertical = referenceFaceVertical;
            cFile.referenceFaceHorizontal = referenceFaceHorizontal;
            cFile.referencePointHorizontal = referencePointHorizontal;
            cFile.referencePointVertical = referencePointVertical;
            cFile.targetHost = targetHost;
            cFile.isHasReference = isHasReference;
          }
          cFile.isAllowSetting = isAllowSetting;
        }
        cFile.fileStream = fileBlob;
        cFile.size = fileBlob?.size;
        res.push(cFile);
      }
    }
  };

  const uploadProps: UploadProps = {
    name: 'file',
    // multiple: true,
    accept: '.zip',
    maxCount: 1,
    showUploadList: false,
    method: 'PUT',
    beforeUpload: async (file: any, fileList: any) => {
      // console.log(file, fileList, '============before');
      file.folderPath = file.webkitRelativePath
        ? file.webkitRelativePath?.replace('/' + file.name, '')
        : '';
      const isZip = file.name?.toLowerCase()?.endsWith('.zip');
      let isValidate: any = true;
      const zipFiles: any = [];
      if (isZip) {
        file.directoryName = file.name?.slice(0, file.name?.lastIndexOf('.'));
        await getZipFiles(file, zipFiles);
        isValidate = await isGmodelerFileWithId(zipFiles, componentData.id);
      } else {
        isValidate = await isGmodelerFileWithId(fileList, componentData.id);
      }
      if (isValidate !== true) {
        if (!isWarning) {
          isWarning = true;
          message.warning(isValidate);
        }
        return false;
      }
      if (isZip) {
        setUploads(zipFiles);
        uploadFileList(zipFiles);
      } else {
        if (file.name?.toLowerCase() === 'meta.json') {
          // const name = await getJsonName(file);
          const {
            name,
            familyType,
            referenceFaceVertical,
            referenceFaceHorizontal,
            targetHost,
            referencePointHorizontal,
            referencePointVertical,
            isAllowSetting,
            isHasReference,
          } = (await getJsonAnother(file)) as any;
          const folderName = file.folderPath.split('/').pop();
          file.folderName = name || folderName;
          file.familyType = familyType;
          if (isAllowSetting) {
            file.referenceFaceVertical = referenceFaceVertical;
            file.referenceFaceHorizontal = referenceFaceHorizontal;
            file.referencePointHorizontal = referencePointHorizontal;
            file.referencePointVertical = referencePointVertical;
            file.targetHost = targetHost;
            file.isHasReference = isHasReference;
          }
          file.isAllowSetting = isAllowSetting;
        }
        if (!file.uid) {
          file.uid = `new-file-${getUUID()}`;
        }
        file.md5 = '';
        file.percent = 0;
        file.status = 'uploading';
        file.fileType = 'gmodeler';
        setUploads(fileList);
        uploadFile(file);
      }
      return false;
    },
  };

  const uploaDirectoryProps = {
    ...uploadProps,
    accept: '.png,.dwg,.glb,.gmr,.json,.dxf',
  };

  const summary = useMemo(() => {
    const jsonFile = uploads?.find((f) => f?.name?.toLowerCase() === 'meta.json');
    const elementName = jsonFile?.folderName;
    const len = uploads.length;
    const doneLength = uploads?.filter((f) => f.status === 'done')?.length;
    if (!uploads?.length) {
      return undefined;
    }
    return {
      fileName: jsonFile?.folderName,
      path: jsonFile?.folderPath,
      uid: `new-file-${getUUID()}`,
      name: elementName,
      process: doneLength / len,
      length: len,
      doneLength: doneLength,
    };
  }, [uploads]);

  // Form 组织详情 更新提交事件
  const onFinish = () => {
    const formValues = form.getFieldsValue();
    // console.log(formValues, '=====formValues');
    const childs =
      uploads?.map((f) => {
        return {
          name: f.name.slice(0, f.name.lastIndexOf('.')),
          storageFileId: f.extra,
          storageId: f.storageId,
          suffix: f.name.split('.').pop() || '',
        };
      }) || [];
    const json = uploads?.find((f) => f?.name?.toLowerCase() === 'meta.json');
    (async function handleUpdateOrganization() {
      setInfo((v) => ({ ...v, saveIsLoading: true }));
      const fileSuffix = fileList?.[0]?.fileSuffix || componentData?.sourceFileSuffix || 'rfa';
      const res: any = await updateComponent({
        appList: formValues?.appList,
        name: formValues?.componentName,
        storageFileId: componentData?.category === 'gmodeler' ? '' : fileList?.[0]?.storageFileId,
        suffix: componentData?.category === 'gmodeler' ? '' : fileSuffix?.toLowerCase(),
        tagList: [formValues?.tagsArr?.[formValues?.tagsArr?.length - 1]],
        componentId: componentData?.id,
        category: componentData?.category || 'common',
        componentRelatedFiles: componentData?.category === 'gmodeler' ? childs : [],
        adsorptionCategory: extData?.adsorptionCategory,
        extData: {
          familyType: json?.familyType || componentData?.familyType || '',
          referenceFaceVertical: extData?.extData?.referenceFaceVertical || '',
          referenceFaceHorizontal: extData?.extData?.referenceFaceHorizontal || '',
          targetHost: extData?.extData?.targetHost || '',
          referencePointVertical: extData?.extData?.referencePointVertical || '',
          referencePointHorizontal: extData?.extData?.referencePointHorizontal || '',
        },
      });
      setInfo((v) => ({ ...v, saveIsLoading: false }));
      if (res?.success) {
        message.success('构件信息已保存', 2);
        getElementsFirst();
        setUpdateInfo((v: any) => ({ ...v, visible: false }));
      }
    })();
  };

  const handleMenuClick = ({ key }: any) => {
    isWarning = false;
    if (key === 'file') {
      fileRef?.current?.click();
    } else {
      folderRef?.current?.click();
    }
  };

  const renderProgress = () => {
    if (componentData?.category !== 'gmodeler') {
      return fileList?.map((file: any) => (
        <div key={file.uid} className={styles.fileItem}>
          <div className={styles.fileIcon}>{renderFileIcon(file)}</div>
          <div className={styles.fileState}>
            <div className={styles.fileDetail} title={file.name || file.fileName}>
              <span className={`${styles.fileName}`}>{file.name || file.fileName}</span>
              <span className={classnames(styles.fileSize, { hide: !file.size })}>
                {(file.size / 1024 / 1024).toFixed(2)}M
              </span>
            </div>
            {file.status === 'uploading' && (
              <Progress
                strokeColor={'#5F4DFF'}
                strokeWidth={4}
                percent={parseInt(file.percent)}
                size="small"
                showInfo={false}
              />
            )}
            {file.status === 'error' && (
              <div className={styles.errorStatus}>
                <WarmingCircle size={16} style={{ color: '#ff4d4f', marginRight: 8 }} />
                {file.response?.message || '上传失败。发生未知错误，请重新上传'}
              </div>
            )}
          </div>
          <div className={styles.deleteFile}>
            <Close
              size={16}
              onClick={() => {
                setFileList((v) => v.filter((item) => item.uid !== file.uid));
              }}
            />
          </div>
        </div>
      ));
    } else {
      if (!summary) {
        return null;
      }
      return (
        <div className={styles.fileItem}>
          <div className={styles.fileIcon}>{renderFileIcon(summary)}</div>
          <div className={styles.fileState}>
            <div className={styles.fileDetail} title={summary?.name}>
              <span className={`${styles.fileName}`}>{summary?.name}</span>
              <span className={classnames(styles.fileSize)}>
                {`${summary?.doneLength}/${summary?.length}`}
              </span>
            </div>
            {summary?.process != 1 && (
              <Progress
                strokeColor={'#5F4DFF'}
                strokeWidth={4}
                percent={parseInt('' + summary?.process)}
                size="small"
                showInfo={false}
              />
            )}
          </div>
          <div className={styles.deleteFile}>
            <Close
              size={16}
              onClick={() => {
                setUploads([]);
              }}
            />
          </div>
        </div>
      );
    }
  };

  const selectProperty = () => {
    setIsShowAdsorptionModal(true);
  };

  const onLocalAdsorptionCategoryChange = (value: string, option: any) => {
    if (value === 'custom') {
      setIsShowAdsorptionModal(true);
      setExtData({
        adsorptionCategory: 'custom',
        extData: {
          referenceFaceVertical: '',
          referenceFaceHorizontal: '',
          targetHost: '',
          referencePointVertical: '',
          referencePointHorizontal: '',
        },
      });
    } else {
      const extend = JSON.parse(option?.extend || '{}');
      setExtData({
        adsorptionCategory: value,
        extData: {
          referenceFaceVertical: extend?.referenceFaceVertical || '',
          referenceFaceHorizontal: extend?.referenceFaceHorizontal || '',
          targetHost: extend?.targetHost || '',
          referencePointVertical: extend?.referencePointVertical || '',
          referencePointHorizontal: extend?.referencePointHorizontal || '',
        },
      });
    }
  };

  useEffect(() => {
    const len = uploads.length;
    const doneLength = uploads?.filter((f) => f.status === 'done')?.length;
    const json = uploads?.find((f) => f?.name?.toLowerCase() === 'meta.json');
    if (len === doneLength && doneLength !== 0) {
      setExtData((d: any) => ({
        adsorptionCategory: json?.isHasReference ? 'custom' : d.adsorptionCategory,
        extData: {
          referenceFaceVertical:
            json?.referenceFaceVertical || d?.extData?.referenceFaceVertical || '',
          referenceFaceHorizontal:
            json?.referenceFaceHorizontal || d?.extData?.referenceFaceHorizontal || '',
          referencePointVertical:
            json?.referencePointVertical || d?.extData?.referencePointVertical || '',
          referencePointHorizontal:
            json?.referencePointHorizontal || d?.extData?.referencePointHorizontal || '',
          targetHost: json?.targetHost || d?.extData?.targetHost || '',
        },
      }));
      setIsAbleSet(!!json?.isAllowSetting);
      if (json?.isHasReference) {
        form.setFieldsValue({
          adsorptionCategory: 'custom',
        });
      }
    }
  }, [uploads]);

  const onChangeAdsorption = (params: any, rows: any) => {
    setExtData((d: any) => ({
      adsorptionCategory: params.adsorptionCategory,
      extData: {
        referenceFaceVertical: params?.extData?.referenceFaceVertical || '',
        referenceFaceHorizontal: params?.extData?.referenceFaceHorizontal || '',
        referencePointVertical: params?.extData?.referencePointVertical || '',
        referencePointHorizontal: params?.extData?.referencePointHorizontal || '',
        targetHost: params?.extData?.targetHost || '',
      },
    }));
    setIsShowAdsorptionModal(false);
    form.setFieldsValue({
      adsorptionCategory: params.adsorptionCategory,
    });
  };

  const onCloseAdsorption = (isEmpty: boolean) => {
    setIsShowAdsorptionModal(false);
    if (isEmpty) {
      setExtData((d: any) => ({
        adsorptionCategory: undefined,
        extData: {},
      }));
      form.setFieldsValue({
        adsorptionCategory: undefined,
      });
    }
  };

  return (
    <>
      <Modal
        {...{
          title: '编辑构件',
          width: 800,
          footer: null,
          visible,
          destroyOnClose: true,
          maskClosable: false,
          onCancel: () => setUpdateInfo((v: any) => ({ ...v, visible: false })),
        }}
      >
        <div className={styles.bodyBox}>
          <div className={styles.left}>
            <div className={styles.img}>
              {fileList?.length > 0 ? (
                <EmptyMemberColor />
              ) : (
                <img src={componentData?.thumbnailUrl} />
              )}
            </div>
          </div>
          <div className={styles.right}>
            <Form
              {...{
                initialValues: {
                  ...componentData,
                  tagsArr,
                  appList: componentData.appIdList,
                  adsorptionCategory: componentData.adsorptionCategory || undefined,
                },
                form,
                name: 'updateComponent',
                labelCol: { flex: '80px' },
                wrapperCol: { flex: 1 },
                onFinish,
                onFieldsChange,
              }}
            >
              <Form.Item label="文件" name="componentFile">
                {componentData?.category === 'gmodeler' ? (
                  <UploadBtn
                    menuClick={handleMenuClick}
                    btnRender={<Button icon={<UploadOutlined />}>重新上传</Button>}
                  />
                ) : (
                  <Upload {...fileProps}>
                    <Button icon={<UploadOutlined />}>重新上传</Button>
                  </Upload>
                )}
                <Upload {...uploadProps} className={styles.hideUploadBtn}>
                  <Button
                    id="project-upload-file"
                    style={{ display: 'none', position: 'absolute' }}
                    ref={fileRef}
                  >
                    上传文件
                  </Button>
                </Upload>
                <Upload {...uploaDirectoryProps} directory className={styles.hideUploadBtn}>
                  <Button
                    id="project-upload-directory"
                    style={{ display: 'none', position: 'absolute' }}
                    ref={folderRef}
                  >
                    上传文件夹
                  </Button>
                </Upload>
                <div>{renderProgress()}</div>
              </Form.Item>

              <Form.Item label="名称" name="componentName" rules={[{ required: true }]}>
                <Input style={{ width: '100%' }} placeholder="请输入名称" />
              </Form.Item>

              <Form.Item
                label="分类"
                name="tagsArr"
                rules={[{ required: true, message: '请选择分类' }]}
              >
                <Cascader
                  {...{
                    style: { width: '100%', maxWidth: '327px' },
                    placeholder: '请选择分类',
                    // multiple: multiTagFileTypes.includes(fileList?.[0]?.fileSuffix || ''),
                    // changeOnSelect: true,
                    // onChange: (value: any, selectedOptions: any) => {
                    //   console.log(selectedOptions);
                    // },
                    options: tags,
                    expandTrigger: 'hover',
                    fieldNames: {
                      label: 'name',
                      value: 'id',
                      children: 'children',
                    },
                    displayRender: (labels: any, selectedOptions: any) => {
                      const nodes = labels.map((labelList: string, i: number) => {
                        const option = selectedOptions[i];
                        if (i === labels.length - 1) {
                          return <span key={option.id}>{labelList}</span>;
                        }
                        return (
                          <span key={option.id}>
                            {labelList} {`>`}{' '}
                          </span>
                        );
                      });
                      return (
                        <div className="ellipsis_txt" title={labels.join('>')}>
                          {nodes}
                        </div>
                      );
                    },
                  }}
                  expandIcon={<></>}
                />
              </Form.Item>

              <Form.Item
                label="行为规则"
                name="adsorptionCategory"
                rules={[{ required: isAbleSet ? true : false, message: '请选择行为规则' }]}
              >
                <Select
                  placeholder="请选择行为规则"
                  onChange={(val: any, option: any) => onLocalAdsorptionCategoryChange(val, option)}
                  style={{ width: '290px' }}
                  options={adsorptionCategorys}
                  showArrow
                  // allowClear
                  disabled={!isAbleSet}
                  popupClassName={styles.adsorptionSelect}
                />
              </Form.Item>

              <Form.Item
                label="应用"
                name="appList"
                rules={[{ required: true, message: '请选择应用' }]}
              >
                <Select
                  mode="multiple"
                  placeholder="请选择应用"
                  style={{ width: '100%' }}
                  options={apps}
                  fieldNames={{
                    label: 'name',
                    value: 'code',
                  }}
                  showArrow
                />
              </Form.Item>

              <Form.Item style={{ textAlign: 'right', marginTop: 100, marginBottom: 0 }}>
                <Button
                  htmlType="button"
                  style={{ margin: '0 8px' }}
                  onClick={() => setUpdateInfo((v: any) => ({ ...v, visible: false }))}
                >
                  取消
                </Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={info.saveIsLoading}
                  disabled={
                    info.saveIsDisabled ||
                    fileList.some((item) => item.status !== 'done') ||
                    (summary && summary.process !== 1)
                  }
                >
                  保存
                </Button>
              </Form.Item>
            </Form>
            <span
              className={`${styles.icon} ${isAbleSet ? '' : styles.disableIcon} ${uploads?.length > 0 || fileList?.length > 0 ? styles.icon1 : ''
                }`}
              onClick={() => {
                if (isAbleSet) {
                  selectProperty();
                }
              }}
            >
              <Property />
            </span>
          </div>
        </div>
      </Modal>
      {isShowAdsorptionModal && (
        <AdsorptionModal
          onChange={onChangeAdsorption}
          onClose={onCloseAdsorption}
          adsorptionCategorys={adsorptionCategorys}
          referencePointHorizontals={referencePointHorizontals}
          referencePointVerticals={referencePointVerticals}
          referenceFaceVerticals={referenceFaceVerticals}
          referenceFaceHorizontals={referenceFaceHorizontals}
          targetHosts={targetHosts}
          data={extData}
        />
      )}
    </>
  );
}

export default React.memo(Index);
